import { TextField } from '@mui/material'
import type { GridColDef } from '@mui/x-data-grid'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import z from 'zod/v3'
import SimpleDataGrid from '~/components/containers/SimpleDataGrid'
import { QualityPageLayout } from '~/components/routes/quality/quality-page-layout'
import {
  buildQualitySharedQuery,
  type QualitySharedSearch,
  qualitySharedSearchSchema,
  resolveQualitySharedFilters,
} from '~/components/routes/quality/quality-search'
import { formatDateTime, QUALITY_QUERY_CACHE } from '~/components/routes/quality/qualityUtils'
import { api } from '~/lib/tuyau'
import { withRoutePermissions } from '~/utils/rbac'
import { formatDateOnly } from '~/utils/types/formatDates'

export const Route = createFileRoute('/_authenticated/quality/articles')({
  staticData: {
    permission: ['read', 'quality'],
  },
  component: QualityArticlesPage,
  validateSearch: qualitySharedSearchSchema.extend({
    articleRef: z.string().optional(),
  }),
})
withRoutePermissions(Route)

const articlesColumnDef = (): GridColDef[] => [
  {
    field: 'checkpointArticleRef',
    headerName: 'Référence',
    width: 220,
  },
  {
    field: 'ean',
    headerName: 'EAN 13',
    width: 180,
  },
  {
    field: 'label',
    headerName: 'Libellé',
    width: 300,
  },
  {
    field: 'dlc',
    headerName: 'DLC',
    valueFormatter: (params) => formatDateOnly(params.value as string),
    width: 140,
  },
  {
    field: 'storeLabel',
    headerName: 'Boutique',
    width: 150,
  },
  {
    field: 'warehouseAt',
    headerName: 'Plateforme',
    valueFormatter: (params) => formatDateTime(params.value as string),
    width: 160,
  },
  {
    field: 'shippingAt',
    headerName: 'Transport',
    valueFormatter: (params) => formatDateTime(params.value as string),
    width: 160,
  },
  {
    field: 'boutiqueAt',
    headerName: 'Boutique',
    valueFormatter: (params) => formatDateTime(params.value as string),
    width: 160,
  },
  {
    field: 'soldAt',
    headerName: 'Vendu',
    valueFormatter: (params) => formatDateTime(params.value as string),
    width: 160,
  },
  {
    field: 'stolenAt',
    headerName: 'Volé',
    valueFormatter: (params) => formatDateTime(params.value as string),
    width: 160,
  },
  {
    field: 'destroyedAt',
    headerName: 'Détruit',
    valueFormatter: (params) => formatDateTime(params.value as string),
    width: 160,
  },
]

function QualityArticlesPage() {
  const navigate = Route.useNavigate()
  const search = Route.useSearch()
  const { articleRef = null } = search

  const filters = resolveQualitySharedFilters(search)
  const { page, pageSize } = filters

  const handleSearchChange = (patch: Partial<QualitySharedSearch>) => {
    navigate({ search: (prev) => ({ ...prev, ...patch }) })
  }

  const {
    data: articles,
    isLoading,
    isFetching,
  } = useQuery(
    api.stores.getArticlesData.queryOptions(
      { query: { ...buildQualitySharedQuery(filters), reference: articleRef ?? undefined } },
      { placeholderData: keepPreviousData, ...QUALITY_QUERY_CACHE },
    ),
  )

  const safeArticles = articles?.data ?? []

  return (
    <QualityPageLayout
      activeTab="articles"
      filters={filters}
      onSearchChange={handleSearchChange}
      searchField={
        <TextField
          variant="outlined"
          label="Recherche par référence"
          value={articleRef ?? ''}
          onChange={(e) =>
            navigate({ search: (prev) => ({ ...prev, articleRef: e.target.value }) })
          }
        />
      }
    >
      <SimpleDataGrid
        columns={articlesColumnDef()}
        rows={safeArticles.map((a) => ({
          id: a.checkpointArticleRef,
          checkpointArticleRef: a.checkpointArticleRef,
          ean: a.ean,
          label: a.label,
          dlc: a.dlc,
          storeLabel: a.storeLabel,
          warehouseAt: a.warehouseAt,
          shippingAt: a.shippingAt,
          boutiqueAt: a.boutiqueAt,
          soldAt: a.soldAt,
          stolenAt: a.stolenAt,
          destroyedAt: a.destroyedAt,
        }))}
        loading={isLoading || isFetching}
        rowCount={articles?.meta.total ?? 0}
        pageSizeOptions={[20, 50]}
        paginationMode="server"
        paginationModel={{ page, pageSize }}
        onPaginationModelChange={(params) => {
          handleSearchChange({ page: params.page, pageSize: params.pageSize })
        }}
        disableRowSelectionOnClick
        checkboxSelection={false}
      />
    </QualityPageLayout>
  )
}
